import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Package } from "lucide-react";
import { api } from "@/lib/api";
import { KitItemRow } from "@/components/KitPanel";
import { StatusBadge } from "@/components/StatusBadge";

const STEPS = ["Placed", "Shipped", "Delivered"];

const Timeline = ({ order }) => {
  const idx = STEPS.indexOf(order.status);
  const dateFor = (s) => order.status_history?.filter((h) => h.status === s).pop()?.at;
  return (
    <ol className="grid grid-cols-3 gap-2" data-testid="order-timeline">
      {STEPS.map((s, i) => (
        <li key={s} className="space-y-3">
          <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#1A73E8]" : "bg-slate-200"}`} />
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full grid place-items-center ${i <= idx ? "bg-[#1A73E8] text-white" : "bg-slate-100 text-slate-400"}`}>
              <Check className="w-3.5 h-3.5" />
            </span>
            <span className={`text-sm font-semibold ${i <= idx ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
          </div>
          {i <= idx && dateFor(s) && <p className="text-xs text-slate-500">{new Date(dateFor(s)).toLocaleDateString()}</p>}
        </li>
      ))}
    </ol>
  );
};

export default function MyOrder() {
  const [order, setOrder] = useState(undefined);

  useEffect(() => {
    api.get("/orders/me").then((r) => setOrder(r.data)).catch(() => setOrder(null));
  }, []);

  if (order === undefined) return <Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" />;

  if (!order) {
    return (
      <div className="max-w-xl py-16 space-y-6" data-testid="no-order-state">
        <Package className="w-10 h-10 text-slate-300" />
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">No order yet</h1>
        <p className="text-slate-600">Build your kit and place your order — you get one.</p>
        <Link to="/" data-testid="no-order-go-store-btn" className="inline-flex h-12 px-6 items-center rounded-full bg-[#1A73E8] text-white font-semibold hover:bg-[#1557B0]">
          Go to store
        </Link>
      </div>
    );
  }

  const d = order.delivery;
  return (
    <div className="space-y-10 animate-rise" data-testid="my-order-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Order <span data-testid="my-order-id">{order.id}</span></p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Your kit is on its way.</h1>
        <div className="mt-4"><StatusBadge status={order.status} testId="my-order-status" /></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8"><Timeline order={order} /></div>
      <div className="grid lg:grid-cols-12 gap-8">
        <section className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
          <h2 className="font-heading text-xl font-bold mb-2">Items</h2>
          <ul className="divide-y divide-slate-100" data-testid="my-order-items">{order.items.map((i, k) => <KitItemRow key={k} item={i} />)}</ul>
          <p className="mt-4 pt-4 border-t border-slate-200 flex justify-between text-sm">
            <span className="text-slate-500">Total</span><span className="font-mono font-semibold">{order.total_points} pts</span>
          </p>
        </section>
        <section className="lg:col-span-5 bg-slate-950 text-white rounded-2xl p-6 sm:p-8 space-y-1 text-sm" data-testid="my-order-delivery">
          <h2 className="font-heading text-xl font-bold mb-3">Delivering to</h2>
          <p className="font-semibold">{d.full_name}</p>
          <p className="text-slate-300">{d.address_line1}</p>
          {d.address_line2 && <p className="text-slate-300">{d.address_line2}</p>}
          <p className="text-slate-300">{d.city}, {d.state} {d.postal_code}</p>
          <p className="text-slate-300">{d.country}</p>
          <p className="text-slate-400 pt-3">{d.phone}</p>
          {d.notes && <p className="text-slate-400 pt-2 italic">“{d.notes}”</p>}
        </section>
      </div>
    </div>
  );
}
